import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import dynamic from 'next/dynamic';
import * as S from './RestoredAdDrawer.styles';
import { promotions } from '@/components/Ad/Promotions/Promotions.constants';
import { getLessPrice } from '@/components/Ad/Promotions/Promotions.helpers';
import { Promotion } from '@/components/Ad/Promotions/Promotion';
import { Drawer } from '@/components/Drawer/Drawer';
import { Button } from '@/components/UI/Button/Button';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { AdStatus } from '@/types';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useServerDataContext } from '@/contexts';
import { AdKind } from '@/api/types/common.types';

interface RestoredAdDrawerProps {
  isOpen: boolean;
  toggle: () => void;
}

const PromotionFlow = dynamic(
  () =>
    import('@/components/Ad/Promotions/PromotionFlow').then(
      mod => mod.PromotionFlow
    ),
  { ssr: false }
);

export const RestoredAdDrawer = ({ isOpen, toggle }: RestoredAdDrawerProps) => {
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const [isDrawerOpen, toggleDrawer] = useToggleOpenState();
  const [selectedType, setSelectedType] = useState<AdKind | null>(null);
  const { isWebView } = useBridgeState();

  function handleSelectPromotion(type: AdKind) {
    setSelectedType(type);
    toggleDrawer();
  }

  return (
    <Drawer
      dataTestId={AdPageTestIds.statusBanner.restoredAdDrawer}
      isHeadFixed={false}
      hasBottomMargin={false}
      isOpen={isOpen}
      close={toggle}
      title={t('pages.ad.ad_is_restored')}
      bottom={
        <Button
          appearance="secondary"
          isFullWidth
          onClick={toggle}
          data-testid={AdPageTestIds.statusBanner.restoredAdDrawerBtn}
        >
          {t('pages.ad.ad_is_restored_button')}
        </Button>
      }
      className="action"
    >
      <S.DrawerItem $isInTheMiddle>
        <S.RestoredAdDescription>
          <Typography.Body $variant="4">
            {t('pages.ad.ad_is_restored_subtitle')}
          </Typography.Body>
        </S.RestoredAdDescription>

        <Typography.Caption $variant="2">
          {t('pages.ad.ad_number')}
        </Typography.Caption>

        <S.AdNumber>{adData?.legacyResourceId}</S.AdNumber>
      </S.DrawerItem>

      <S.DrawerItem $isInTheMiddle>
        <S.PromotionTitle>
          <Typography.Title>
            {t('pages.ad.ad_is_restored_promotion')}
          </Typography.Title>
        </S.PromotionTitle>
        {adData &&
          promotions(
            t,
            getLessPrice(adData.bumpServices[0]),
            getLessPrice(adData.vipServices[0]),
            getLessPrice(adData.featuredServices[0]),
            adData.status === AdStatus.Published
          ).map(promotion => (
            <Promotion
              key={promotion.type}
              promotion={promotion}
              onSelect={handleSelectPromotion}
            />
          ))}

        <PromotionFlow
          isOpen={isDrawerOpen}
          toggle={toggleDrawer}
          onSuccessPayment={toggle}
          type={selectedType}
          hideSuccessModal={!isWebView}
        />
      </S.DrawerItem>
    </Drawer>
  );
};
